// เช็คชื่อหอพักก่อนนอน — หน้าที่ครูหอพัก (ใต้ฝ่ายปกครอง) ถือมือถือเดินทีละหลังหลังอีซา
//
// แทนกระดาษเช็คชื่อ ครูเปิดเขตของตัวเอง เดินทีละหลัง กดเฉพาะคนที่ไม่อยู่ แล้วกด
// "ยืนยันหลังนี้" คนที่ไม่ได้ถูกกดนับว่าอยู่ ถ้าบังคับติ๊กทีละคนครูจะกลับไปใช้กระดาษ
// ส่วนปุ่มยืนยันรายหลังทำให้ "หลังนี้ครบ" มีชื่อคนกับเวลากำกับ ซึ่งกระดาษไม่มี
//
// คนที่ระบบรู้แน่ว่าไม่อยู่ (ห้องพยาบาล / กลับบ้านออกประตูไปแล้ว) ลงให้เองและแก้ไม่ได้
// ส่วนที่ระบบแค่สงสัย (ออกนอกยังไม่กลับ, ครบกำหนดกลับแล้ว) ตั้งเป็นค่าเริ่มต้นให้ ครูเปลี่ยนได้
// เพราะระบบรู้ว่าเด็กมีใบ แต่ไม่รู้ว่าเด็กยืนอยู่ตรงหน้าครูหรือเปล่า
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Form, ProgressBar, Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import io from 'socket.io-client';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import { getToken } from '../services/authorize';
import { toThaiDateLong } from '../utils/thaiDate';

const API = process.env.REACT_APP_API || 'http://localhost:3002/api';
const UPLOAD_BASE = API.replace(/\/api\/?$/, '').replace(/\/$/, '');
const photoUrl = (photo) => (photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null);

const STATUS = {
  present: { text: 'อยู่', variant: 'success' },
  absent: { text: 'ไม่อยู่', variant: 'danger' },
  leave: { text: 'ลา', variant: 'secondary' },
  nurse: { text: 'ห้องพยาบาล', variant: 'info' },
};
// ปุ่มที่ครูกดได้ — nurse มาจากห้องพยาบาลเท่านั้น
const CHOICES = ['present', 'absent', 'leave'];

const timeOf = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toTimeString().slice(0, 5);
};

const apiError = (error) => error?.response?.data?.message || 'ลองใหม่อีกครั้ง';

const Avatar = ({ photo, name }) => {
  const src = photoUrl(photo);
  return src ? (
    <img src={src} alt={name} width={40} height={40} className="rounded-circle flex-shrink-0" style={{ objectFit: 'cover' }} />
  ) : (
    <span className="rounded-circle bg-secondary bg-opacity-25 d-inline-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: 40, height: 40 }}>
      <i className="ri-user-line text-secondary"></i>
    </span>
  );
};

// ค่าบนจอก่อนครูแตะ: ผลที่ยืนยันไว้แล้ว > คำแนะนำของระบบ > อยู่
const initialStatus = (student) => {
  if (student.record && student.record.source === 'teacher') return student.record.status;
  return student.suggest || 'present';
};

/* ── ภาพรวมทุกเขต ─────────────────────────────────────────────────────────── */

const VillageCard = ({ village, onOpen }) => {
  const done = village.houses > 0 && village.confirmed_houses >= village.houses;
  const pct = village.houses ? Math.round((village.confirmed_houses / village.houses) * 100) : 0;
  return (
    <Card className="card-one mb-2" role="button" onClick={() => onOpen(village.village_id)}>
      <Card.Body className="p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <div className="fw-semibold flex-fill">
            {village.village_name}
            <span className="fs-xs text-secondary ms-2">{village.village_gender === 'm' ? 'ชาย' : 'หญิง'} · {village.students} คน</span>
          </div>
          {village.missing > 0 && <Badge bg="danger">ยังไม่พบตัว {village.missing}</Badge>}
          {done && village.missing === 0 && <Badge bg="success"><i className="ri-check-line"></i> ครบแล้ว</Badge>}
        </div>
        <ProgressBar now={pct} variant={done ? 'success' : 'primary'} style={{ height: 6 }} />
        <div className="d-flex fs-xs text-secondary mt-1">
          <span className="flex-fill">ยืนยันแล้ว {village.confirmed_houses}/{village.houses} หลัง</span>
          {village.absent > 0 && <span className="text-danger">ไม่อยู่ {village.absent} คน</span>}
        </div>
        {village.staff.length > 0 && (
          <div className="fs-xs text-secondary mt-1"><i className="ri-user-star-line me-1"></i>{village.staff.join(', ')}</div>
        )}
      </Card.Body>
    </Card>
  );
};

const Overview = ({ onOpen }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [denied, setDenied] = useState('');
  const [showOthers, setShowOthers] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axiosInstance.get(`${API}/dormitory/night-check`);
      setData(res);
    } catch (error) {
      // 403 = ยังไม่มีใครมอบหมายเขตให้ — บอกว่าต้องไปหาใคร ไม่ใช่ขึ้นกล่อง error
      if (error?.response?.status === 403) setDenied(apiError(error));
      else Swal.fire('อ่านภาพรวมไม่สำเร็จ', apiError(error), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, { auth: { token: getToken() } });
    socket.on('dormNightcheck:update', load);
    return () => socket.disconnect();
  }, [load]);

  if (denied) {
    return (
      <div className="text-center text-secondary py-5">
        <i className="ri-lock-line d-block mb-2" style={{ fontSize: 40 }}></i>
        {denied}
      </div>
    );
  }
  if (!data) return <div className="text-center py-5"><Spinner animation="border" /></div>;

  const mine = data.villages.filter((v) => v.mine);
  const others = data.villages.filter((v) => !v.mine);
  const totals = data.villages.reduce((acc, v) => ({
    houses: acc.houses + v.houses,
    confirmed: acc.confirmed + v.confirmed_houses,
    missing: acc.missing + v.missing,
  }), { houses: 0, confirmed: 0, missing: 0 });

  const section = (title, list) => list.length > 0 && (
    <>
      <div className="fs-sm fw-semibold text-secondary mt-3 mb-2">{title}</div>
      {list.map((v) => <VillageCard key={v.village_id} village={v} onOpen={onOpen} />)}
    </>
  );

  return (
    <>
      <Card className="card-one mb-2">
        <Card.Body className="p-3 d-flex align-items-center gap-3">
          <div className="flex-fill">
            <div className="fs-xs text-secondary">{data.all ? 'ทั้งโรงเรียน' : 'เขตที่รับผิดชอบ'}</div>
            <div className="fs-5 fw-semibold">{totals.confirmed}/{totals.houses} หลัง</div>
            <div className="fs-xs text-secondary">ควรเสร็จก่อน {data.deadline} น.</div>
          </div>
          {totals.missing > 0 && (
            <div className="text-danger text-end">
              <div className="fs-5 fw-semibold">{totals.missing}</div>
              <div className="fs-xs">ยังไม่พบตัว</div>
            </div>
          )}
          <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading} aria-label="รีเฟรช">
            {loading ? <Spinner size="sm" animation="border" /> : <i className="ri-refresh-line"></i>}
          </Button>
        </Card.Body>
      </Card>
      {section('เขตที่ฉันรับผิดชอบ', mine)}
      {/* ครูหอพักได้มาแค่เขตของตัวเองอยู่แล้ว ส่วนฝ่ายปกครองเห็นทุกเขต — พับไว้ก่อน
          ไม่งั้น 37 เขตเต็มจอมือถือทั้งที่งานประจำคืนนี้คือเขตของตัวเอง */}
      {others.length > 0 && (mine.length === 0 || showOthers
        ? section(mine.length ? 'เขตอื่น' : 'ทุกเขต', others)
        : (
          <Button variant="outline-secondary" className="w-100 mt-2" onClick={() => setShowOthers(true)}>
            แสดงเขตอื่น ({others.length} เขต)
          </Button>
        ))}
    </>
  );
};

/* ── รายหลังในเขต ─────────────────────────────────────────────────────────── */

const StudentRow = ({ student, value, editable, onChange, onFound }) => {
  const rec = student.record;
  return (
    <li className="list-group-item py-2 px-2">
      <div className="d-flex align-items-center gap-2">
        <Avatar photo={student.st_photo} name={student.st_name} />
        <div className="flex-fill min-width-0">
          <div className="text-truncate">{student.st_name}</div>
          <div className="fs-xs text-secondary">{student.st_idnumber}</div>
        </div>
        {student.locked && <Badge bg={STATUS[student.locked.status].variant}>{STATUS[student.locked.status].text}</Badge>}
      </div>

      {(student.locked || student.hints.length > 0 || student.isha_absent) && (
        <div className="d-flex flex-wrap gap-1 mt-1">
          {student.locked && <span className="fs-xs text-secondary">{student.locked.reason}</span>}
          {student.isha_absent && <Badge bg="warning" text="dark">ขาดละหมาดอีซา</Badge>}
          {student.hints.map((h) => <Badge key={h} bg="light" text="dark" className="border">{h}</Badge>)}
        </div>
      )}

      {!student.locked && editable && (
        <ButtonGroup size="sm" className="w-100 mt-2">
          {CHOICES.map((s) => (
            <Button key={s} variant={value === s ? STATUS[s].variant : `outline-${STATUS[s].variant}`}
              onClick={() => onChange(student.st_id, s)}>
              {STATUS[s].text}
            </Button>
          ))}
        </ButtonGroup>
      )}
      {!student.locked && !editable && rec && (
        <Badge bg={STATUS[rec.status].variant} className="mt-1">{STATUS[rec.status].text}</Badge>
      )}

      {rec?.status === 'absent' && (
        rec.found_at ? (
          <div className="fs-xs text-success mt-1">
            <i className="ri-user-received-line me-1"></i>
            พบตัวแล้ว {timeOf(rec.found_at)} น. โดย {rec.found_by_name || '-'}{rec.found_note ? ` · ${rec.found_note}` : ''}
          </div>
        ) : (
          <Button size="sm" variant="outline-success" className="mt-2 w-100" onClick={() => onFound(student)}>
            <i className="ri-user-received-line me-1"></i>พบตัวแล้ว
          </Button>
        )
      )}
    </li>
  );
};

const HouseCard = ({ house, drafts, editable, saving, onChange, onConfirm, onFound }) => {
  const valueOf = (s) => (s.locked ? s.locked.status : drafts[s.st_id] ?? initialStatus(s));
  const absent = house.students.filter((s) => !s.locked && valueOf(s) === 'absent');
  const changed = house.students.some((s) => !s.locked && s.record?.status !== valueOf(s));
  const confirmed = !!house.confirmed_at;
  // มีเด็กที่เพิ่งจัดเข้าหลังนี้หลังจากยืนยันไปแล้ว = ต้องยืนยันใหม่
  const stale = confirmed && house.students.some((s) => !s.record);

  return (
    <Card className={`card-one mb-2 ${confirmed && !changed && !stale ? 'border-success' : ''}`}>
      <Card.Header className="d-flex align-items-center gap-2 py-2">
        <Card.Title as="h6" className="mb-0 flex-fill">หลัง {house.dorm_name}
          <span className="fs-xs text-secondary fw-normal ms-2">{house.students.length} คน</span>
        </Card.Title>
        {confirmed
          ? <span className="fs-xs text-success"><i className="ri-check-double-line"></i> {timeOf(house.confirmed_at)} น. {house.confirmed_by}</span>
          : <Badge bg="light" text="dark" className="border">ยังไม่ยืนยัน</Badge>}
      </Card.Header>
      <ul className="list-group list-group-flush">
        {house.students.map((s) => (
          <StudentRow key={s.st_id} student={s} value={valueOf(s)} editable={editable}
            onChange={onChange} onFound={onFound} />
        ))}
      </ul>
      {editable && (!confirmed || changed || stale) && (
        <Card.Footer className="p-2">
          <Button className="w-100" variant={absent.length ? 'danger' : 'success'} disabled={saving}
            onClick={() => onConfirm(house, absent, valueOf)}>
            {saving ? <Spinner size="sm" animation="border" /> : (
              absent.length
                ? <><i className="ri-alarm-warning-line me-1"></i>ยืนยันหลังนี้ · ไม่อยู่ {absent.length} คน</>
                : <><i className="ri-check-line me-1"></i>{confirmed ? 'ยืนยันใหม่' : 'ยืนยันหลังนี้ · อยู่ครบ'}</>
            )}
          </Button>
        </Card.Footer>
      )}
    </Card>
  );
};

const VillageView = ({ villageId, onBack }) => {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingDorm, setSavingDorm] = useState(null);
  const [hideDone, setHideDone] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await axiosInstance.get(`${API}/dormitory/night-check/villages/${villageId}`);
      setData(res);
      return res;
    } catch (error) {
      Swal.fire('อ่านรายชื่อไม่สำเร็จ', apiError(error), 'error');
      return null;
    }
  }, [villageId]);

  useEffect(() => { setData(null); setDrafts({}); load(); }, [load]);

  // ครูอีกคนในเขตเดียวกันยืนยันหลังไหน — โหลดใหม่แล้วล้างค่าที่กดค้างไว้เฉพาะหลังนั้น
  // หลังอื่นที่ครูคนนี้กดค้างอยู่ต้องไม่หาย
  useEffect(() => {
    const socket = io(process.env.REACT_APP_SOCKET_URL, { auth: { token: getToken() } });
    socket.on('dormNightcheck:update', async (payload) => {
      if (Number(payload?.village_id) !== Number(villageId)) return;
      const res = await load();
      const house = res?.houses.find((h) => h.dorm_id === payload.dorm_id);
      if (!house) return;
      setDrafts((prev) => {
        const next = { ...prev };
        house.students.forEach((s) => { delete next[s.st_id]; });
        return next;
      });
    });
    return () => socket.disconnect();
  }, [villageId, load]);

  const editable = data && data.date === data.tonight;

  const onChange = (stId, status) => setDrafts((prev) => ({ ...prev, [stId]: status }));

  const onConfirm = async (house, absent, valueOf) => {
    if (absent.length) {
      const ok = await Swal.fire({
        icon: 'warning',
        title: `ไม่อยู่ ${absent.length} คน`,
        html: `<div class="text-start">${absent.map((s) => `• ${s.st_name}`).join('<br/>')}</div>
               <div class="mt-2 fs-sm">ระบบจะแจ้งครูหอพักของเขตและฝ่ายปกครองทันที</div>`,
        showCancelButton: true,
        confirmButtonText: 'ยืนยัน',
        cancelButtonText: 'กลับไปแก้',
        confirmButtonColor: '#dc3545',
      });
      if (!ok.isConfirmed) return;
    }
    setSavingDorm(house.dorm_id);
    try {
      const entries = house.students
        .filter((s) => !s.locked)
        .map((s) => ({ st_id: s.st_id, status: valueOf(s) }))
        .filter((e) => e.status !== 'present');
      await axiosInstance.post(`${API}/dormitory/night-check/houses/${house.dorm_id}/confirm`, {
        date: data.date, entries,
      });
      setDrafts((prev) => {
        const next = { ...prev };
        house.students.forEach((s) => { delete next[s.st_id]; });
        return next;
      });
      await load();
    } catch (error) {
      Swal.fire('บันทึกไม่สำเร็จ', apiError(error), 'error');
    } finally {
      setSavingDorm(null);
    }
  };

  const onFound = async (student) => {
    const { isConfirmed, value } = await Swal.fire({
      title: `พบตัว ${student.st_name}`,
      input: 'text',
      inputPlaceholder: 'พบที่ไหน / หมายเหตุ (ไม่บังคับ)',
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
    });
    if (!isConfirmed) return;
    try {
      await axiosInstance.post(`${API}/dormitory/night-check/students/${student.st_id}/found`, {
        date: data.date, note: value || null,
      });
      await load();
    } catch (error) {
      Swal.fire('บันทึกไม่สำเร็จ', apiError(error), 'error');
    }
  };

  const houses = useMemo(() => {
    if (!data) return [];
    return hideDone ? data.houses.filter((h) => !h.confirmed_at) : data.houses;
  }, [data, hideDone]);

  if (!data) return <div className="text-center py-5"><Spinner animation="border" /></div>;

  const confirmedCount = data.houses.filter((h) => h.confirmed_at).length;

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-2">
        <Button variant="outline-secondary" size="sm" onClick={onBack}><i className="ri-arrow-left-line"></i></Button>
        <div className="flex-fill">
          <div className="fw-semibold">{data.village.village_name}</div>
          <div className="fs-xs text-secondary">ยืนยันแล้ว {confirmedCount}/{data.houses.length} หลัง</div>
        </div>
        <Form.Check type="switch" id="hide-done" label="ซ่อนที่ยืนยันแล้ว" className="fs-sm"
          checked={hideDone} onChange={(e) => setHideDone(e.target.checked)} />
      </div>

      {!editable && (
        <Alert variant="light" className="fs-sm">ดูอย่างเดียว — ยืนยันได้เฉพาะคืนนี้</Alert>
      )}

      {houses.length === 0 ? (
        <div className="text-center text-secondary py-4">
          <i className="ri-checkbox-circle-line d-block mb-2" style={{ fontSize: 40 }}></i>
          ยืนยันครบทุกหลังแล้ว
        </div>
      ) : houses.map((h) => (
        <HouseCard key={h.dorm_id} house={h} drafts={drafts} editable={editable}
          saving={savingDorm === h.dorm_id} onChange={onChange} onConfirm={onConfirm} onFound={onFound} />
      ))}
    </>
  );
};

export default function NightCheck() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const [params, setParams] = useSearchParams();
  const villageId = Number(params.get('village')) || null;
  const [tonight, setTonight] = useState(null);

  useEffect(() => {
    axiosInstance.get(`${API}/dormitory/night-check`)
      .then(({ data }) => setTonight(data.tonight))
      .catch(() => {}); // 403 แสดงที่ Overview แล้ว
  }, []);

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="mb-3">
          <h4 className="main-title mb-0">เช็คชื่อหอก่อนนอน</h4>
          <div className="fs-xs text-secondary">
            {tonight ? `คืนวันที่ ${toThaiDateLong(tonight)}` : ''} · กดเฉพาะคนที่ไม่อยู่ แล้วยืนยันทีละหลัง
          </div>
        </div>

        {villageId
          ? <VillageView villageId={villageId} onBack={() => setParams({})} />
          : <Overview onOpen={(id) => setParams({ village: String(id) })} />}

        <Footer />
      </div>
    </>
  );
}
